import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../helpers/axiosInstance";
import useAuth from "../../../../hooks/useAuth";
import { date, money } from "../../../../helpers/format";
import { currentFinancialYear, financialYears } from "../../../../helpers/financialYear";
import { Async, Banner, Button, Card, Confirm, Dialog, Empty, Field, Page, Pill, SectionTitle, useAction, useFetch } from "../../../../shared/ui/ui";
import "./deposits.css";

// The fund's fixed and recurring deposits, as the app shows them. Each keeps
// its own members and their amounts: their part of an FD, or what they put in
// each month for an RD. An RD's monthly deposits are added one month at a
// time. Closing either records the interest the bank paid as one interest
// record and writes each member's part onto the deposit, which is what
// dividends are worked out from.

const KINDS = {
  fixed: {
    short: "FD",
    title: "Fixed deposits",
    feature: "fixed_deposits",
    api: "fixed_deposits",
    route: "fixed-deposits",
    amountField: "amount",
    amountLabel: "Amount (₹)",
    category: "FIXED DEPOSIT",
  },
  recurring: {
    short: "RD",
    title: "Recurring deposits",
    feature: "recurring_deposits",
    api: "recurring_deposits",
    route: "recurring-deposits",
    amountField: "recurring_amount",
    amountLabel: "Recurring amount (₹ a month)",
    category: "RECURRING DEPOSIT",
  },
};

const pick = (body) => body?.data;
const paise = (value) => Math.round((Number(value) || 0) * 100);
const plain = (value) => (Number.isInteger(value) ? String(value) : value.toFixed(2));
const dayInput = (value) => new Date(value || Date.now()).toISOString().slice(0, 10);
const oneYearOn = () => {
  const day = new Date();
  day.setFullYear(day.getFullYear() + 1);
  return day.getTime();
};

/** Whole calendar months from `start` to `end`. */
const monthsBetween = (start, end) => {
  if (!start || !end) return 0;
  const from = new Date(start);
  const to = new Date(end);
  let months = (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth();
  if (to.getDate() < from.getDate()) months -= 1;
  return Math.max(0, months);
};

/** "12 months", or "444 days" when it is not a whole number of months. */
const termLabel = (start, end) => {
  if (!start || !end) return "—";
  const months = monthsBetween(start, end);
  const from = new Date(start);
  // Same day of the month: whole months, whatever the time of day each was saved at.
  if (months > 0 && from.getDate() === new Date(end).getDate()) return months === 1 ? "1 month" : `${months} months`;
  const days = Math.round((new Date(end) - from) / 86400000);
  return days === 1 ? "1 day" : `${days} days`;
};

/** Interest from `from` to `to` at `rate`% a year, compounded quarterly — a guide. */
const estimateInterest = (amount, rate, from, to) => {
  const days = (new Date(to) - new Date(from)) / 86400000;
  return amount > 0 && days > 0 ? amount * (Math.pow(1 + rate / 400, days / 91.25) - 1) : 0;
};

/** A deposit's tenure as saved, or worked out from its dates. */
const tenureOf = (deposit) => deposit.tenure_months ?? monthsBetween(deposit.start_date, deposit.maturity_date);
const tenureLabel = (months) => (months === 1 ? "1 month" : `${months} months`);

/** The start date (a yyyy-mm-dd value) plus `months`, as one. */
const addMonths = (day, months) => {
  const [year, month, date] = day.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1 + months, date));
  return next.toISOString().slice(0, 10);
};

/** What a deposit should earn by maturity: an FD's one sum, or every month of an RD's. */
const expectedInterest = (recurring, amount, rate, start, maturity, months = monthsBetween(start, maturity)) => {
  if (!recurring) return estimateInterest(amount, rate, start, maturity);
  const first = new Date(start);
  let total = 0;
  for (let month = 0; month < months; month++) {
    const paid = new Date(first.getFullYear(), first.getMonth() + month, first.getDate());
    total += estimateInterest(amount, rate, paid, maturity);
  }
  return total;
};

/**
 * `interest` split in proportion to `weights`, to the paisa, adding up
 * exactly — the server's split, shown before it is made.
 */
const splitInterest = (weights, interest) => {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (!weights.length || total <= 0) return weights.map(() => 0);
  const whole = paise(interest);
  const parts = weights.map((weight) => Math.floor((whole * weight) / total));
  let largest = 0;
  weights.forEach((weight, index) => {
    if (weight > weights[largest]) largest = index;
  });
  parts[largest] += whole - parts.reduce((sum, part) => sum + part, 0);
  return parts.map((part) => part / 100);
};

const useKind = (recurring) => {
  const { organisation } = useAuth();
  const kind = KINDS[recurring ? "recurring" : "fixed"];
  return { ...kind, base: `/api/${kind.api}/${organisation._id}`, recurring };
};

// ---------------------------------------------------------------- list

export const DepositsList = ({ recurring = false }) => {
  const navigate = useNavigate();
  const kind = useKind(recurring);
  const { auth, can } = useAuth();
  const deposits = useFetch(kind.base, { select: pick });
  const [closed, setClosed] = useState(false);
  const everyone = can(kind.feature, "view_all");
  const me = String(auth?.user?._id || "");
  const mineOf = (deposit) => deposit.members.find((m) => String(m.member_id) === me);
  // The server already sends a member only their own RD totals.
  const amountFor = (deposit) => (recurring ? deposit.total_deposited : everyone ? deposit.amount : mineOf(deposit)?.amount || 0);

  return (
    <Page
      title={kind.title}
      subtitle={everyone ? `Every ${kind.short} the fund holds, and whose money is in it.` : `The ${kind.short}s your money is in.`}
      actions={
        <>
          <Button onClick={() => navigate(`/admin/${kind.route}/interest`)}>{everyone ? "Interest by member" : `Your ${kind.short} interest`}</Button>
          {can(kind.feature, "add") ? (
            <Button variant="primary" onClick={() => navigate(`/admin/${kind.route}/new`)}>
              Add {kind.short}
            </Button>
          ) : null}
        </>
      }
    >
      <Async state={deposits}>
        {(list = []) => {
          const active = list.filter((deposit) => deposit.status === "active");
          const shown = list.filter((deposit) => (deposit.status === "active") !== closed);
          return (
            <>
              <Card>
                <p className="sf-stat-label">
                  {recurring ? (everyone ? "Deposited in active RDs" : "You have deposited") : everyone ? "In active FDs" : "Your money in FDs"}
                </p>
                <p className="sf-stat-value">{money(active.reduce((sum, deposit) => sum + amountFor(deposit), 0))}</p>
                <p className="sf-muted" style={{ margin: 0 }}>
                  {active.length === 1 ? `1 active ${kind.short}` : `${active.length} active ${kind.short}s`}
                </p>
              </Card>
              <div className="sf-chips" style={{ margin: "0.5rem 0 1rem" }}>
                <Button variant={closed ? "secondary" : "primary"} onClick={() => setClosed(false)}>
                  Active
                </Button>
                <Button variant={closed ? "primary" : "secondary"} onClick={() => setClosed(true)}>
                  Closed
                </Button>
              </div>
              {!shown.length ? (
                <Empty title={closed ? `No closed ${kind.short}s` : `No active ${kind.short}s`}>
                  {closed ? `${kind.short}s appear here once they are closed.` : `${kind.short}s the fund holds appear here.`}
                </Empty>
              ) : (
                <Card flush>
                  {shown.map((deposit) => {
                    const mine = everyone ? null : mineOf(deposit);
                    const interest = everyone ? deposit.interest_earned : mine?.interest;
                    return (
                      <button key={deposit._id} className="sf-row sf-row-button" onClick={() => navigate(`/admin/${kind.route}/${deposit._id}`)}>
                        <div className="sf-row-main">
                          <p className="sf-row-title">{deposit.name}</p>
                          <p className="sf-row-sub">
                            {recurring
                              ? `${money(everyone ? deposit.recurring_amount : mine?.amount)} a month · ${deposit.installments.length} of ${tenureOf(deposit)} months`
                              : `${deposit.interest_rate}% a year · ${tenureLabel(tenureOf(deposit))}`}
                            {" · "}
                            {deposit.status === "active"
                              ? `matures ${date(deposit.maturity_date)}`
                              : `closed ${date(deposit.closed_on)}, ${everyone ? "" : "your "}interest ${money(interest)}`}
                          </p>
                        </div>
                        <p className="sf-row-title sf-row-end">{money(amountFor(deposit))}</p>
                      </button>
                    );
                  })}
                </Card>
              )}
            </>
          );
        }}
      </Async>
    </Page>
  );
};

// ---------------------------------------------------------------- add / edit

export const DepositForm = ({ recurring = false }) => {
  const { depositId } = useParams();
  const kind = useKind(recurring);
  const editing = useFetch(depositId ? `${kind.base}/${depositId}` : null, { select: pick, skip: !depositId });
  const title = `${depositId ? "Edit" : "New"} ${recurring ? "recurring" : "fixed"} deposit`;

  return (
    <Page title={title} back>
      {depositId ? <Async state={editing}>{(deposit) => <FormBody key={deposit?._id} kind={kind} deposit={deposit} />}</Async> : <FormBody kind={kind} />}
    </Page>
  );
};

const FormBody = ({ kind, deposit }) => {
  const navigate = useNavigate();
  const { organisation } = useAuth();
  const directory = useFetch(`/api/member/${organisation._id}/getallminimalmembers`);
  const { busy, run } = useAction();
  const { recurring, short } = kind;
  const [name, setName] = useState(deposit?.name || "");
  const [amount, setAmount] = useState(deposit ? plain(deposit[kind.amountField]) : "");
  const [rate, setRate] = useState(deposit ? plain(deposit.interest_rate) : "");
  const [start, setStart] = useState(dayInput(deposit?.start_date));
  const [maturity, setMaturity] = useState(dayInput(deposit?.maturity_date || oneYearOn()));
  const [tenure, setTenure] = useState(deposit ? String(tenureOf(deposit)) : "12");
  const [comment, setComment] = useState(deposit?.comment || "");
  const [search, setSearch] = useState("");
  // member id -> amount typed, for the members in it.
  const [shares, setShares] = useState(() => Object.fromEntries((deposit?.members || []).map((m) => [String(m.member_id), plain(m.amount)])));
  const [error, setError] = useState(null);
  // An FD once added, and an RD with a month deposited, keep their members and
  // amounts: that money has already come out of their savings.
  const locked = recurring ? (deposit?.installments?.length || 0) > 0 : Boolean(deposit);

  const total = Number(amount) || 0;
  const startMs = new Date(start).getTime();
  const maturityMs = new Date(maturity).getTime();
  const months = parseInt(tenure, 10) || 0;
  // Tenure and the two dates move together: a tenure sets the maturity date
  // from the start, and a maturity date picked by hand sets the tenure.
  const changeTenure = (value) => {
    setTenure(value);
    const count = parseInt(value, 10);
    if (count > 0) setMaturity(addMonths(start, count));
  };
  const changeStart = (value) => {
    setStart(value);
    if (value && months > 0) setMaturity(addMonths(value, months));
  };
  const changeMaturity = (value) => {
    setMaturity(value);
    if (value) setTenure(String(monthsBetween(startMs, new Date(value).getTime())));
  };
  const allocated = Object.values(shares).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const balanced = total > 0 && paise(allocated) === paise(total);

  const splitEqually = (ids = Object.keys(shares)) => {
    if (!ids.length || total <= 0) return;
    const whole = paise(total);
    const each = Math.floor(whole / ids.length);
    let left = whole - each * ids.length;
    setShares(Object.fromEntries(ids.map((id) => [id, plain((each + (left-- > 0 ? 1 : 0)) / 100)])));
  };
  const toggle = (id) =>
    setShares((current) => {
      const next = { ...current };
      if (id in next) delete next[id];
      else next[id] = "";
      return next;
    });

  const save = async () => {
    const problem = !name.trim()
      ? `Give the ${short} a name.`
      : total <= 0
      ? `Enter the ${recurring ? "recurring amount" : "FD amount"}.`
      : rate === ""
      ? "Enter the interest rate."
      : !(maturityMs > startMs)
      ? "The maturity date must be after the start date."
      : recurring && months < 1
      ? "A recurring deposit must run for at least a month."
      : !Object.keys(shares).length
      ? `Choose the members whose money is in this ${short}.`
      : Object.values(shares).some((value) => !(Number(value) > 0))
      ? "Enter an amount for every member."
      : !balanced
      ? `The members' amounts add up to ${money(allocated)}, not ${money(total)}.`
      : null;
    setError(problem);
    if (problem) return;

    const body = {
      name: name.trim(),
      [kind.amountField]: total,
      interest_rate: Number(rate),
      start_date: startMs,
      maturity_date: maturityMs,
      tenure_months: months,
      comment: comment.trim() || undefined,
      members: Object.entries(shares).map(([member_id, value]) => ({ member_id, amount: Number(value) })),
    };
    const saved = await run(() => (deposit ? axiosInstance.put(`${kind.base}/${deposit._id}`, body) : axiosInstance.post(kind.base, body)), {
      done: (data) => data.message || "Saved",
    });
    if (saved) navigate(`/admin/${kind.route}/${saved.data?._id || deposit?._id}`, { replace: true });
  };

  return (
    <Async state={directory}>
      {(active = []) => {
        // Only active members can be picked; someone already in it who has
        // since become inactive stays, since their money is still in it.
        const listed = new Set(active.map((person) => String(person._id)));
        const people = [
          ...active,
          ...(deposit?.members || [])
            .filter((member) => !listed.has(String(member.member_id)))
            .map((member) => ({ _id: member.member_id, full_name: `${member.name} (inactive)` })),
        ];
        const shown = people.filter((person) => (person.full_name || "").toLowerCase().includes(search.toLowerCase()));
        const allPicked = active.length > 0 && active.every((person) => String(person._id) in shares);
        return (
          <>
            <Card>
              <div className="fd-form">
                <Field label={`${recurring ? "Recurring" : "Fixed"} deposit name`}>
                  <input value={name} onChange={(event) => setName(event.target.value)} placeholder={recurring ? "Post office RD 2026" : "SBI FD 2026"} />
                </Field>
                <Field label={kind.amountLabel}>
                  <input type="number" min="0" step="0.01" value={amount} disabled={locked} onChange={(event) => setAmount(event.target.value)} />
                </Field>
                <Field label="Interest (% a year)">
                  <input type="number" min="0" step="0.01" value={rate} onChange={(event) => setRate(event.target.value)} />
                </Field>
                <Field label="Tenure (months)" help={recurring ? "One deposit each month" : undefined}>
                  <input type="number" min="1" step="1" value={tenure} onChange={(event) => changeTenure(event.target.value)} />
                </Field>
                <Field label="Start date">
                  <input type="date" value={start} onChange={(event) => changeStart(event.target.value)} />
                </Field>
                <Field label="Maturity date">
                  <input type="date" value={maturity} onChange={(event) => changeMaturity(event.target.value)} />
                </Field>
                <Field label="Remarks (optional)">
                  <input value={comment} onChange={(event) => setComment(event.target.value)} />
                </Field>
              </div>
              <p className="sf-muted" style={{ margin: 0 }}>
                {maturityMs > startMs
                  ? [
                      termLabel(startMs, maturityMs),
                      recurring ? `${months} monthly deposits` : null,
                      total > 0 ? `about ${money(expectedInterest(recurring, total, Number(rate) || 0, startMs, maturityMs, months))} interest, compounded quarterly` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "The maturity date must be after the start date."}
              </p>
            </Card>

            <SectionTitle
              title={`Members in this ${short}`}
              action={
                locked ? null : (
                  <Button variant="text" onClick={() => splitEqually()} disabled={!Object.keys(shares).length || total <= 0}>
                    Split equally
                  </Button>
                )
              }
            />
            <p className="sf-muted">
              {locked
                ? recurring
                  ? "Months have been deposited from their savings, so the members and their amounts are fixed."
                  : "Their money has been taken from their savings, so the members and their amounts are fixed. Delete the FD and add it again to change them."
                : recurring
                ? "Only the members whose money goes in each month. Their monthly amounts must add up to the recurring amount, and each month's part is taken from their savings when it is deposited."
                : "Only the members whose money is in this FD. Their amounts must add up to the FD amount, and are taken from their savings when the FD is added. It goes back when the FD closes."}
            </p>
            <Card flush>
              {locked ? null : (
                <div className="fd-member-tools">
                  <input placeholder="Search members" value={search} onChange={(event) => setSearch(event.target.value)} />
                  <label className="fd-check">
                    <input
                      type="checkbox"
                      checked={allPicked}
                      onChange={() => (allPicked ? setShares({}) : splitEqually(active.map((person) => String(person._id))))}
                    />
                    Select all
                  </label>
                </div>
              )}
              {(locked ? shown.filter((person) => String(person._id) in shares) : shown).map((person) => {
                const id = String(person._id);
                const picked = id in shares;
                return (
                  <div key={id} className="sf-row fd-member-row">
                    <label className="fd-check sf-row-main">
                      <input type="checkbox" checked={picked} disabled={locked} onChange={() => toggle(id)} />
                      {person.full_name}
                    </label>
                    {picked ? (
                      <input
                        className="fd-share-input"
                        type="number"
                        min="0"
                        step="0.01"
                        aria-label={`Amount for ${person.full_name}`}
                        value={shares[id]}
                        disabled={locked}
                        onChange={(event) => setShares({ ...shares, [id]: event.target.value })}
                      />
                    ) : null}
                  </div>
                );
              })}
              <div className="sf-row">
                <p className="sf-row-main sf-row-title" style={{ color: balanced ? undefined : "var(--error-2)" }}>
                  {money(allocated)} of {money(total)}
                  {recurring ? " a month" : ""}
                </p>
                <p className="sf-row-sub sf-row-end">{Object.keys(shares).length} chosen</p>
              </div>
            </Card>

            {error ? <Banner tone="bad" title={error} /> : null}
            <Button variant="primary" block busy={busy} onClick={save}>
              {deposit ? "Save changes" : `Add ${recurring ? "recurring" : "fixed"} deposit`}
            </Button>
          </>
        );
      }}
    </Async>
  );
};

// ---------------------------------------------------------------- one deposit

export const DepositDetail = ({ recurring = false }) => {
  const { depositId } = useParams();
  const navigate = useNavigate();
  const kind = useKind(recurring);
  const { auth, can } = useAuth();
  const deposit = useFetch(`${kind.base}/${depositId}`, { select: pick });
  const { busy, run } = useAction();
  const [confirming, setConfirming] = useState(null);
  const [interest, setInterest] = useState("");
  const [closedOn, setClosedOn] = useState(dayInput());
  const [monthOn, setMonthOn] = useState(dayInput());
  const everyone = can(kind.feature, "view_all");
  const me = String(auth?.user?._id || "");

  const act = async (request, after) => {
    const ok = await run(request, { done: (data) => data.message || "Done" });
    if (ok) {
      setConfirming(null);
      after ? after() : deposit.reload();
    }
  };

  return (
    <Async state={deposit}>
      {(data) => {
        if (!data?._id) {
          return (
            <Page title={kind.title} back>
              <Empty title={`${kind.short} not found`} />
            </Page>
          );
        }
        const active = data.status === "active";
        const months = tenureOf(data);
        const installments = data.installments || [];
        const full = recurring && installments.length >= months;
        const weights = data.members.map((m) => (recurring ? m.deposited || 0 : m.amount));
        const typed = interest === "" ? null : Number(interest);
        const parts = typed == null || !(typed >= 0) ? null : splitInterest(weights, typed);
        const expected = expectedInterest(recurring, recurring ? data.recurring_amount : data.amount, data.interest_rate, data.start_date, data.maturity_date, months);
        const monthly = everyone ? data.recurring_amount : data.members.find((m) => String(m.member_id) === me)?.amount || 0;
        return (
          <Page title={data.name} back>
            <Card>
              <div className="sf-row" style={{ padding: 0, border: 0 }}>
                <div className="sf-row-main">
                  <p className="sf-stat-label">{recurring ? (everyone ? "Total deposited" : "You have deposited") : "Amount"}</p>
                  <p className="sf-stat-value">{money(recurring ? data.total_deposited : data.amount)}</p>
                  {recurring ? (
                    <p className="sf-muted" style={{ margin: 0 }}>
                      {installments.length} of {months} months · {money(monthly)} a month
                    </p>
                  ) : null}
                </div>
                <Pill tone={active ? "warn" : "good"}>{active ? "Active" : "Closed"}</Pill>
              </div>
              {recurring && months > 0 ? (
                <div className="sf-progress" aria-label="Months deposited">
                  <span style={{ width: `${Math.min(100, (installments.length / months) * 100)}%` }} />
                </div>
              ) : null}
              <dl className="sf-kv" style={{ marginTop: "1rem" }}>
                {recurring ? (
                  <>
                    <dt>Recurring amount</dt>
                    <dd>{money(data.recurring_amount)} a month</dd>
                  </>
                ) : null}
                <dt>Interest</dt>
                <dd>{data.interest_rate}% a year</dd>
                <dt>Start date</dt>
                <dd>{date(data.start_date)}</dd>
                <dt>Maturity date</dt>
                <dd>{date(data.maturity_date)}</dd>
                <dt>Tenure</dt>
                <dd>{tenureLabel(months)}</dd>
                {active ? (
                  <>
                    <dt>Expected interest</dt>
                    <dd>{money(expected)} (estimate)</dd>
                  </>
                ) : (
                  <>
                    <dt>Closed</dt>
                    <dd>{date(data.closed_on)}</dd>
                    <dt>Interest earned</dt>
                    <dd>
                      <strong>{money(data.interest_earned)}</strong>
                    </dd>
                  </>
                )}
                {data.comment ? (
                  <>
                    <dt>Remarks</dt>
                    <dd>{data.comment}</dd>
                  </>
                ) : null}
              </dl>
            </Card>

            {recurring && active && can(kind.feature, "edit") ? (
              <Button variant="primary" block disabled={full} onClick={() => setConfirming("month")}>
                {full ? "Every month is deposited" : `Add month ${installments.length + 1} deposit · ${money(data.recurring_amount)}`}
              </Button>
            ) : null}

            <SectionTitle title={everyone ? `Members in this ${kind.short}` : "Your share"} />
            {/* What each has in it — an RD's deposits so far — and their share. */}
            <MembersTable
              members={recurring ? data.members.map((m) => ({ ...m, amount: m.deposited || 0 })) : data.members}
              total={recurring ? data.total_deposited : data.amount}
              amountLabel={recurring ? "In RD" : "In FD"}
              interests={active ? null : data.members.map((m) => m.interest)}
            />

            {recurring ? (
              <>
                <SectionTitle title="Monthly deposits" />
                {!installments.length ? (
                  <p className="sf-muted">No month has been deposited yet.</p>
                ) : (
                  <Card flush>
                    {installments
                      .map((installment, index) => ({ installment, number: index + 1 }))
                      .reverse()
                      .map(({ installment, number }) => (
                        <div key={installment._id} className="sf-row">
                          <div className="sf-row-main">
                            <p className="sf-row-title">Month {number}</p>
                            <p className="sf-row-sub">{date(installment.date)}</p>
                          </div>
                          <p className="sf-row-title sf-row-end">{money(installment.amount)}</p>
                          {active && can(kind.feature, "delete") ? (
                            <Button variant="text" onClick={() => setConfirming({ takeBack: installment, number })}>
                              Take back
                            </Button>
                          ) : null}
                        </div>
                      ))}
                  </Card>
                )}
              </>
            ) : null}

            {!active && data.interest_record_id ? (
              <p className="sf-muted">
                The interest is one record in Interest records, under {kind.category}. Each member's part above is what their dividend is worked out
                from.
              </p>
            ) : null}

            <div className="sf-chips" style={{ margin: "0.5rem 0 1rem" }}>
              {active && can(kind.feature, "edit") ? (
                <>
                  <Button variant={recurring ? "secondary" : "primary"} disabled={!weights.some((w) => w > 0)} onClick={() => setConfirming("close")}>
                    Close {kind.short}
                  </Button>
                  <Button onClick={() => navigate(`/admin/${kind.route}/${data._id}/edit`)}>Edit</Button>
                </>
              ) : null}
              {!active && can(kind.feature, "delete") ? <Button onClick={() => setConfirming("reopen")}>Reopen {kind.short}</Button> : null}
              {active && can(kind.feature, "delete") ? (
                <Button variant="danger" onClick={() => setConfirming("delete")}>
                  Delete
                </Button>
              ) : null}
            </div>

            {confirming === "month" ? (
              <Dialog
                title={`Deposit month ${installments.length + 1} of ${months}?`}
                onClose={busy ? undefined : () => setConfirming(null)}
                actions={
                  <>
                    <Button variant="text" onClick={() => setConfirming(null)} disabled={busy}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      busy={busy}
                      onClick={() => act(() => axiosInstance.post(`${kind.base}/${data._id}/installments`, { date: new Date(monthOn).getTime() }))}
                    >
                      Deposit {money(data.recurring_amount)}
                    </Button>
                  </>
                }
              >
                <p className="sf-stat-label">Amount</p>
                <p className="sf-stat-value">{money(data.recurring_amount)}</p>
                <Field label="Deposited on">
                  <input type="date" value={monthOn} onChange={(event) => setMonthOn(event.target.value)} />
                </Field>
                <p className="sf-muted">Taken from each member's savings:</p>
                <dl className="sf-kv">
                  {data.members.map((member) => (
                    <React.Fragment key={String(member.member_id)}>
                      <dt>{member.name}</dt>
                      <dd>{money(member.amount)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </Dialog>
            ) : null}
            {confirming?.takeBack ? (
              <Confirm
                title={`Take back month ${confirming.number}?`}
                confirmLabel="Take back"
                danger
                busy={busy}
                onCancel={() => setConfirming(null)}
                onConfirm={() => act(() => axiosInstance.delete(`${kind.base}/${data._id}/installments/${confirming.takeBack._id}`))}
              >
                The {money(confirming.takeBack.amount)} deposited on {date(confirming.takeBack.date)} comes off the RD and goes back into each member's
                savings.
              </Confirm>
            ) : null}
            {confirming === "close" ? (
              <Dialog
                title={`Close ${data.name}`}
                onClose={busy ? undefined : () => setConfirming(null)}
                actions={
                  <>
                    <Button variant="text" onClick={() => setConfirming(null)} disabled={busy}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      busy={busy}
                      disabled={parts == null}
                      onClick={() =>
                        act(() =>
                          axiosInstance.post(`${kind.base}/${data._id}/close`, {
                            interest_earned: typed,
                            closed_on: new Date(closedOn).getTime(),
                          })
                        )
                      }
                    >
                      {parts == null ? `Close ${kind.short}` : `Close with ${money(typed)} interest`}
                    </Button>
                  </>
                }
              >
                <p className="sf-muted">
                  Enter the interest the bank actually paid. Expected was about {money(expected)}. What each member put in goes back into their savings;
                  the interest goes to Interest records.
                </p>
                <div className="fd-form">
                  <Field label="Interest earned (₹)">
                    <input type="number" min="0" step="0.01" autoFocus value={interest} onChange={(event) => setInterest(event.target.value)} />
                  </Field>
                  <Field label="Closed on">
                    <input type="date" value={closedOn} onChange={(event) => setClosedOn(event.target.value)} />
                  </Field>
                </div>
                {parts ? (
                  <>
                    <p className="sf-stat-label" style={{ marginTop: "1rem" }}>
                      How it splits{recurring ? ", by what each deposited" : ""}
                    </p>
                    <MembersTable
                      members={data.members.map((m, index) => ({ ...m, amount: weights[index], share: undefined }))}
                      total={weights.reduce((sum, weight) => sum + weight, 0)}
                      amountLabel={recurring ? "Deposited" : "In FD"}
                      interests={parts}
                    />
                  </>
                ) : null}
              </Dialog>
            ) : null}
            {confirming === "reopen" ? (
              <Confirm
                title={`Reopen ${data.name}?`}
                confirmLabel="Reopen"
                busy={busy}
                onCancel={() => setConfirming(null)}
                onConfirm={() => act(() => axiosInstance.post(`${kind.base}/${data._id}/reopen`))}
              >
                It becomes active again: what each member put in comes out of their savings again, its interest record of {money(data.interest_earned)}{" "}
                is removed from Interest records, and each member's interest is cleared.
              </Confirm>
            ) : null}
            {confirming === "delete" ? (
              <Confirm
                title={`Delete ${data.name}?`}
                confirmLabel="Delete"
                danger
                busy={busy}
                onCancel={() => setConfirming(null)}
                onConfirm={() => act(() => axiosInstance.delete(`${kind.base}/${data._id}`), () => navigate(`/admin/${kind.route}`, { replace: true }))}
              >
                What each member put in goes back into their savings, and it will no longer appear in {kind.title}.
              </Confirm>
            ) : null}
          </Page>
        );
      }}
    </Async>
  );
};

/** Members, what each has in it and their share, and their interest when there is some. */
const MembersTable = ({ members, total, amountLabel, interests }) => (
  <Card flush>
    <table className="sf-table">
      <thead>
        <tr>
          <th>Member</th>
          <th className="sf-num">{amountLabel}</th>
          <th className="sf-num">Share</th>
          {interests ? <th className="sf-num">Interest</th> : null}
        </tr>
      </thead>
      <tbody>
        {members.map((member, index) => (
          <tr key={String(member.member_id)}>
            <td>{member.name}</td>
            <td className="sf-num">{money(member.amount)}</td>
            <td className="sf-num">{(member.share ?? (total > 0 ? (member.amount / total) * 100 : 0)).toFixed(1)}%</td>
            {interests ? (
              <td className="sf-num">
                <strong>{money(interests[index])}</strong>
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

// ---------------------------------------------------------------- interest by member

export const DepositInterestByMember = ({ recurring = false }) => {
  const kind = useKind(recurring);
  const { can, financialYearStartMonth } = useAuth();
  const years = useMemo(() => financialYears(financialYearStartMonth).reverse(), [financialYearStartMonth]);
  const [year, setYear] = useState(() => currentFinancialYear(financialYearStartMonth).year);
  const chosen = years.find((entry) => entry.year === year) || years[0];
  const rows = useFetch(`${kind.base}/interest_by_member?from=${chosen.startdate}&to=${chosen.enddate}`, { select: pick });

  return (
    <Page
      title={can(kind.feature, "view_all") ? `${kind.short} interest by member` : `Your ${kind.short} interest`}
      subtitle={`Interest from the ${kind.short}s closed in a financial year — what dividends are worked out from.`}
      back
    >
      <div className="sf-filter-bar">
        <select value={year} onChange={(event) => setYear(Number(event.target.value))} aria-label="Financial year">
          {years.map((entry) => (
            <option key={entry.year} value={entry.year}>
              {entry.label}
            </option>
          ))}
        </select>
      </div>
      <Async state={rows}>
        {(list = []) =>
          !list.length ? (
            <Empty title={`No ${kind.short} interest`}>No {kind.short} was closed in this financial year.</Empty>
          ) : (
            <>
              <Card>
                <p className="sf-stat-label">{kind.short} interest this year</p>
                <p className="sf-stat-value">{money(list.reduce((sum, row) => sum + row.interest, 0))}</p>
              </Card>
              <Card flush>
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th className="sf-num">{recurring ? "Deposited" : `In ${kind.short}s`}</th>
                      <th className="sf-num">{kind.short}s</th>
                      <th className="sf-num">Interest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row) => (
                      <tr key={String(row.member_id)}>
                        <td>{row.name}</td>
                        <td className="sf-num">{money(row.amount)}</td>
                        <td className="sf-num">{row.deposits}</td>
                        <td className="sf-num">
                          <strong>{money(row.interest)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )
        }
      </Async>
    </Page>
  );
};
